import {
  bindings,
  sync,
  syncAll,
  insertSlides,
  insertJoinInstructions,
  insertFlowSlides,
  clearResponses
} from '@polleverywhere/slideware-bindings'
import store from '@polleverywhere/redux'
import { explorer, slice as activitiesSlice } from '../../apps/activities/slice'
import { loadActivityModels } from '../../dispatchers/slideware/placeholder_preload'
import { convertActivityFlowToSlideSet } from './utils/convert_activity_flow_to_slide_set'
import fetchActivity from '../../util/fetch_activity'
import ErrorModel from '../../models/error'

function finishAction (page, options = { }) {
  this.set({
    page,
    pageAction: null
  }, options)
}

function finishWithError (error) {
  this.set({
    page: 'error',
    pageAction: null,
    error
  })
}

function areSlidesInsertable () {
  // README: Avoid calling this at the top of the action().
  // We want to avoid calling the binding layer as much as possible.
  let insertable
  try {
    insertable = bindings.slidesInsertable()
  } catch (ex) {
    console.err('intentionally catching error for background route rollbars')
  }
  return insertable || false
}

export default class State extends Backbone.Model {
  get defaults () {
    return {
      page: null, // sync | syncAll | insert | support | edit
      pageAction: null, // retry
      activity: null,
      // Goog_presenter shows 'inserting' UI, which is indicated by a progress bar.
      // In the future, we are going to remove this an send inserting progress back. (Pivotal: #166997437)
      showInsertComplete: true,
      insertionType: 'activities', // activities | polls | surveys | images (slideware-bindings:insertSlides)
      mode: 'standalone' // standalone | integration
    }
  }

  initialize (attrs = {}, options = {}) {
    this.on('change:page', (model, page, options = {}) => this.action(model, page, options))
    this.on('change:pageAction', (model, page, options = {}) => {
      switch (model.get('pageAction')) {
        case 'retry':
          model.set('pageAction', null)
          this.action(model, model.get('page'), options)
          break
        default:
      }
    })
  }

  async action (model, page, options = {}) {
    switch (page) {
      case 'background':
        // Nothing to do for the background page
        break

      case 'sync':
        try {
          if (!await areSlidesInsertable()) { return }
          let result = await fetchActivity(model.get('activity'))
          if (result instanceof ErrorModel) {
            throw new Error(result.get('xhr').responseJSON.error.message)
          }
          result = [result].flat()
          result && result[0] && await sync(result[0])
          finishAction.call(this, 'close')
        } catch (message) {
          finishWithError.call(this, new ErrorModel({ code: 'Sync error', message }))
        }
        break

      case 'syncAll':
        try {
          if (!await areSlidesInsertable()) { return }
          await syncAll()
          finishAction.call(this, 'close')
        } catch (err) {
          const message = err && err.responseJSON && err.responseJSON.error ? err.responseJSON.error.message : err
          finishWithError.call(this, new ErrorModel({ code: 'Sync all error', message }))
        }
        break

      case 'insertJoinInstructions':
        try {
          if (!await areSlidesInsertable()) { return }
          await insertJoinInstructions()
          finishAction.call(this, 'done', { slideCount: 1 })
        } catch (err) {
          const message = err && err.responseJSON && err.responseJSON.error ? err.responseJSON.error.message : err
          finishWithError.call(this, new ErrorModel({ code: 'Insert join instructions error', message }))
        }
        break

      case 'insert':
        try {
          if (!await areSlidesInsertable()) { return }

          const state = store.getState()
          const activity = activitiesSlice.selectActivityAsModel(state)
          const activities = explorer.selectSelectedEntities(state)

          const skipApiFetch = activity && activity.id !== -1
          const activityModels = skipApiFetch
            ? [activity]
            : await loadActivityModels(activities)

          const flow = model.get('flow')
          model.unset('flow')
          let slideCount = 1

          // If there's a flow defined, we'll... go with the flow. Eh? Get it? :badjokeeel:
          if (flow && flow.steps.length > 0) {
            const slideset = (await Promise.all(activityModels.map(activity => convertActivityFlowToSlideSet(flow, activity))))[0]
            await insertFlowSlides(slideset)
            slideCount = slideset.reduce((total, slideset) => total + slideset.slideData.length, 0)
          } else {
            await insertSlides(activityModels, { insertionType: model.get('insertionType') })
            slideCount = activityModels.length
          }

          // We aren't actually done inserting. We have only finished downloading the images from the server and passed
          // the info to slideware to insert.
          // Goog_presenter shows 'inserting' UI, which is indicated by a progress bar.
          // In the future, we are going to remove this an send inserting progress back. (Pivotal: #166997437)
          if (!model.get('showInsertComplete')) {
            bindings.closeWindow()
            return
          }

          finishAction.call(this, 'done', { slideCount })
        } catch (message) {
          model.set('slidesets', null)
          finishWithError.call(this, new ErrorModel({ code: 'Insert slides error', message }))
        }
        break

      case 'clearResponses':
        try {
          if (!await areSlidesInsertable()) { return }
          await clearResponses(model.get('activity'), options.archiveName)
          finishAction.call(this, 'clearResponsesDone')
        } catch (message) {
          finishWithError.call(this, new ErrorModel({ code: 'Clear responses error', message }))
        }
        break

      default: break
    }
  }

  inspect () {
    return super.toJSON()
  }
}
